/**
 * CustomFieldsEditor (#2866)
 *
 * Editor for vendor-defined custom fields on the chatbot pre-chat lead form. Each field has a
 * label, a type (text / radio / dropdown), a required flag, and — for radio/dropdown — a set of
 * options. Every option may carry an optional "AI prompt" that the backend injects into the bot
 * when the visitor selects it (e.g. a "Federal" option that tells the bot to redirect the visitor
 * to a specific email). Stored under customizationdata.customFields and read by the widget.
 */

import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export type CustomFieldType = 'text' | 'radio' | 'select';

export interface CustomFieldOption {
  label: string;
  value: string;
  prompt?: string;
}

export interface CustomFieldDef {
  id: string;
  label: string;
  type: CustomFieldType;
  required?: boolean;
  options?: CustomFieldOption[];
  prompt?: string;
}

interface CustomFieldsEditorProps {
  fields: CustomFieldDef[];
  onChange: (fields: CustomFieldDef[]) => void;
  maxFields?: number;
}

const genId = () => `cf_${Math.random().toString(36).slice(2, 9)}`;

const TYPE_OPTIONS: { value: CustomFieldType; label: string }[] = [
  { value: 'radio', label: 'Multiple choice (radio)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'text', label: 'Text input' },
];

export const CustomFieldsEditor = ({ fields, onChange, maxFields = 10 }: CustomFieldsEditorProps) => {
  const update = (id: string, patch: Partial<CustomFieldDef>) =>
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const remove = (id: string) => onChange(fields.filter((f) => f.id !== id));

  const add = () => {
    if (fields.length >= maxFields) return;
    onChange([
      ...fields,
      { id: genId(), label: '', type: 'radio', required: false, options: [{ label: '', value: '', prompt: '' }] },
    ]);
  };

  const updateOption = (fieldId: string, index: number, patch: Partial<CustomFieldOption>) =>
    onChange(
      fields.map((f) => {
        if (f.id !== fieldId) return f;
        const options = [...(f.options || [])];
        const next = { ...options[index], ...patch };
        // Keep value in sync with the visible label so the widget + backend match reliably.
        next.value = (patch.label ?? next.label ?? '').trim();
        options[index] = next;
        return { ...f, options };
      }),
    );

  const addOption = (fieldId: string) =>
    onChange(
      fields.map((f) => (f.id === fieldId
        ? { ...f, options: [...(f.options || []), { label: '', value: '', prompt: '' }] }
        : f)),
    );

  const removeOption = (fieldId: string, index: number) =>
    onChange(
      fields.map((f) => (f.id === fieldId
        ? { ...f, options: (f.options || []).filter((_, i) => i !== index) }
        : f)),
    );

  return (
    <div className="flex w-full flex-col gap-3">
      {fields.map((field) => {
        const isChoice = field.type === 'radio' || field.type === 'select';
        return (
          <div key={field.id} className="rounded-lg border border-gray-200 p-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex min-w-[180px] flex-1 flex-col gap-1">
                <Label className="text-xs text-gray-500">Field label</Label>
                <Input
                  value={field.label}
                  placeholder="e.g. Organization type"
                  onChange={(e) => update(field.id, { label: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-gray-500">Type</Label>
                <select
                  className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm outline-none focus:border-primary"
                  value={field.type}
                  onChange={(e) => {
                    const type = e.target.value as CustomFieldType;
                    update(field.id, {
                      type,
                      options: type === 'text' ? undefined : (field.options || [{ label: '', value: '', prompt: '' }]),
                    });
                  }}
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Checkbox
                  id={`cf-req-${field.id}`}
                  checked={!!field.required}
                  onCheckedChange={(checked) => update(field.id, { required: checked === true })}
                />
                <Label htmlFor={`cf-req-${field.id}`} className="cursor-pointer text-sm">Required</Label>
              </div>
              <button
                type="button"
                onClick={() => remove(field.id)}
                className="pb-2 text-sm text-red-600 hover:text-red-700"
                aria-label="Remove field"
              >
                Remove
              </button>
            </div>

            {isChoice && (
              <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3">
                <Label className="text-xs text-gray-500">
                  Options — each can carry an optional AI prompt the bot follows when selected
                </Label>
                {(field.options || []).map((opt, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <Input
                      className="min-w-[140px] flex-1"
                      value={opt.label}
                      placeholder="Option label (e.g. Federal)"
                      onChange={(e) => updateOption(field.id, i, { label: e.target.value })}
                    />
                    <Input
                      className="min-w-[220px] flex-[2]"
                      value={opt.prompt || ''}
                      placeholder="Optional AI prompt when selected (e.g. tell them to email sales@…)"
                      onChange={(e) => updateOption(field.id, i, { prompt: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(field.id, i)}
                      className="text-sm text-gray-400 hover:text-red-600"
                      aria-label="Remove option"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addOption(field.id)}
                  className="self-start text-sm font-medium text-primary hover:underline"
                >
                  + Add option
                </button>
              </div>
            )}
          </div>
        );
      })}

      {fields.length < maxFields && (
        <button
          type="button"
          onClick={add}
          className="self-start rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:border-primary hover:text-primary"
        >
          + Add custom field
        </button>
      )}
    </div>
  );
};

/** Strip empty/incomplete fields + options before persisting. */
export const sanitizeCustomFields = (fields: CustomFieldDef[]): CustomFieldDef[] =>
  (Array.isArray(fields) ? fields : [])
    .map((f) => {
      const isChoice = f.type === 'radio' || f.type === 'select';
      const options = isChoice
        ? (f.options || [])
          .map((o) => ({ label: (o.label || '').trim(), value: (o.label || '').trim(), prompt: (o.prompt || '').trim() || undefined }))
          .filter((o) => o.label)
        : undefined;
      return { ...f, label: (f.label || '').trim(), options };
    })
    .filter((f) => f.label && (f.type === 'text' || (f.options && f.options.length > 0)));
